import { useIntl } from 'react-intl';
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';
import { Params } from 'next/dist/server/router';
import { captureException } from '@sentry/nextjs';

import { CarouselSection } from '@components/CarouselSection';
import { RecipeDetails } from '@components/RecipeDetails';
import { RecipeInfo } from '@components/RecipeInfo';
import { RecipeListCard } from '@components/RecipeListCard';
import { RecipeStructuredData } from '@components/RecipeStructuredData';
import { SplitSection } from '@components/SplitSection';
import { WhereToBuyBadge } from '@components/WhereToBuyBadge';

import { getAllRecipePageSlugs, getRecipePage } from '@data/sanity/queries';
import { recipeMessages } from '@data/ui-messages/recipes';
import { CmsRecipePageDocument } from '@interfaces/common';
import { replaceUnderscoreWithDash } from '@utils/locale';
import { withSiteWideData } from '@utils/page';

interface RecipePageProps {
	recipePage: CmsRecipePageDocument;
}

const Recipe: React.FC<CmsRecipePageDocument> = ({ recipePage }) => {
	const {
		image,
		totalTime,
		serves,
		difficulty,
		categories,
		cuisine,
		product: {
			metadata: { title: productName },
			category: { highlightColor },
		},
		ingredients,
		instructions,
		metadata: { title, description },
		productRecipesPage,
		productRecipes,
		pageSettings: { whereToBuyLink },
	} = recipePage;
	const intl = useIntl();

	return (
		<>
			<RecipeStructuredData {...recipePage} />

			<SplitSection
				backgroundColor={highlightColor}
				footerNode={
					<RecipeInfo
						difficulty={difficulty}
						serves={serves}
						totalTime={totalTime}
					/>
				}
				heading={title}
				headingLevel={1}
				image={image}
				imageOverlayNode={
					<WhereToBuyBadge
						backgroundColor={highlightColor}
						title={intl.formatMessage(recipeMessages.whereToBuy)}
						url={whereToBuyLink}
					/>
				}
				imagePosition={{ desktop: 'right', mobile: 'top' }}
				paragraph={description}
				subheading={productName}
			/>

			<RecipeDetails
				categories={categories}
				cuisine={cuisine}
				ingredients={ingredients}
				instructions={instructions}
			/>

			{productRecipes && productRecipes.length > 0 ? (
				<CarouselSection
					backgroundColor={{ hex: '#F2F2F4', alpha: 1 }}
					carousel={productRecipes}
					heading={intl.formatMessage(recipeMessages.moreProductRecipes, {
						product: productName,
					})}
					link={{
						text: intl.formatMessage(recipeMessages.allProductRecipes, {
							product: productName,
						}),
						url: `/recipes/product/${productRecipesPage.metadata.slug}`,
					}}
					renderItem={RecipeListCard}
				/>
			) : null}
		</>
	);
};

export const getStaticProps: GetStaticProps = withSiteWideData<RecipePageProps>(
	async (context: GetStaticPropsContext) => {
		const params = context.params as Params;
		const { locale } = context;
		const { slug } = params;

		const recipePage = await getRecipePage(locale, slug);

		if (!recipePage) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				metadata: recipePage.metadata,
				newsletterTheme: 'light',
				recipePage,
			},
		};
	}
);

export const getStaticPaths: GetStaticPaths = async () => {
	try {
		const paths = await getAllRecipePageSlugs();

		return {
			paths: paths.map(({ locale, params }) => ({
				locale: replaceUnderscoreWithDash(locale),
				params,
			})),
			fallback: false,
		};
	} catch (error) {
		captureException(error);
		return {
			paths: [],
			fallback: false,
		};
	}
};

export default Recipe;
